import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { History, Clock, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HistoryModal = ({ open, patientId, sectionName, sectionLabel, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && patientId && sectionName) {
      fetchHistory();
    }
  }, [open, patientId, sectionName]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/patients/${patientId}/history/${sectionName}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load edit history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History size={20} className="text-cyan-700" />
            Edit History: {sectionLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-700"></div>
              <p className="mt-2 text-slate-600">Loading history...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8">
              <History size={48} className="mx-auto text-slate-300 mb-2" />
              <p className="text-slate-600">No edit history found for this section</p>
            </div>
          ) : (
            history.map((log, index) => (
              <div
                key={log.id || index}
                className="p-4 bg-slate-50 rounded-lg border border-slate-200"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-cyan-700" />
                    <span className="font-semibold text-slate-900">
                      {log.user_name}
                    </span>
                    <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs rounded">
                      {log.user_role}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-slate-600">
                    <Clock size={14} />
                    {formatDate(log.timestamp)}
                  </div>
                </div>
                <div className="text-sm text-slate-700">
                  <span className="font-medium">Action:</span> {log.action}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={onClose} className="btn-secondary">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HistoryModal;
